import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaDownload,
  FaSpinner,
  FaExclamationTriangle,
  FaExpand,
  FaCompress,
} from "react-icons/fa";
import { Document, Page, pdfjs } from "react-pdf";
import toast from "react-hot-toast";
import { buildApiUrl } from "../../utils/apiConfig";

// Set worker source
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ pyqId, title, onClose, isDarkMode }) => {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);

  const pdfUrl = buildApiUrl(`/api/pyqs/view/${pyqId}`);

  // Handle resize for responsive PDF
  useEffect(() => {
    const handleResize = () => {
      const container = containerRef.current;
      if (container) {
        const width = container.clientWidth - 40;
        setPageWidth(Math.min(width, 900));
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  };

  const onDocumentLoadError = (err) => {
    console.error("PDF load error:", err);
    setLoading(false);
    setError(true);
    toast.error("Failed to load PDF");
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      window.open(buildApiUrl(`/api/pyqs/download/${pyqId}`), "_blank");
      toast.success("Download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  const getBgColor = () => (isDarkMode ? "#1a1a2e" : "#ffffff");
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBorderColor = () => (isDarkMode ? "#3a3d44" : "#e0e0e0");

  // Render all pages in a scrollable container
  const renderAllPages = () => {
    const pages = [];
    for (let i = 1; i <= numPages; i++) {
      pages.push(
        <div
          key={`page-${i}`}
          style={{
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Page
            pageNumber={i}
            width={pageWidth * scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <div
                style={{
                  width: pageWidth * scale,
                  height: (pageWidth * scale * 1.414) / 1.5,
                  background: isDarkMode ? "#2a2a3a" : "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaSpinner
                  style={{
                    animation: "spin 1s linear infinite",
                    color: "#8b5cf6",
                  }}
                />
              </div>
            }
          />
        </div>,
      );
    }
    return pages;
  };

  return (
    <AnimatePresence>
      <motion.div
        key="pdf-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.9)",
          backdropFilter: "blur(8px)",
          display: "flex",
          flexDirection: "column",
          zIndex: 10000,
        }}
        onClick={onClose}
      >
        <motion.div
          key="pdf-modal"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          style={{
            background: getBgColor(),
            borderRadius: "20px",
            margin: "1rem",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.75rem 1.5rem",
              borderBottom: `1px solid ${getBorderColor()}`,
              background: isDarkMode ? "#1e1e2d" : "#f8f9fa",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            <h3
              style={{
                color: getTextColor(),
                margin: 0,
                fontSize: "1rem",
                fontWeight: 600,
                flex: 1,
              }}
            >
              {title}
            </h3>

            {/* Zoom Controls */}
            <div
              style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
            >
              <button
                onClick={zoomOut}
                disabled={scale <= 0.5}
                style={{
                  padding: "0.3rem 0.8rem",
                  background: scale <= 0.5 ? "#6c757d" : "#8b5cf6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: scale <= 0.5 ? "not-allowed" : "pointer",
                  fontSize: "0.8rem",
                }}
              >
                -
              </button>
              <span style={{ color: getTextColor(), fontSize: "0.8rem" }}>
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                disabled={scale >= 2.5}
                style={{
                  padding: "0.3rem 0.8rem",
                  background: scale >= 2.5 ? "#6c757d" : "#8b5cf6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: scale >= 2.5 ? "not-allowed" : "pointer",
                  fontSize: "0.8rem",
                }}
              >
                +
              </button>
            </div>

            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                onClick={toggleFullscreen}
                style={{
                  padding: "0.4rem 0.8rem",
                  background: "#8b5cf6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  fontSize: "0.8rem",
                }}
              >
                {isFullscreen ? <FaCompress /> : <FaExpand />}
                {isFullscreen ? "Exit" : "Fullscreen"}
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  padding: "0.4rem 1rem",
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.8rem",
                }}
              >
                {downloading ? (
                  <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <FaDownload />
                )}
                Download
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: "0.4rem 1rem",
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.8rem",
                }}
              >
                <FaTimes /> Close
              </button>
            </div>
          </div>

          {/* Scrollable PDF Viewer Body */}
          <div
            ref={containerRef}
            style={{
              flex: 1,
              overflow: "auto",
              padding: "1.5rem",
              background: isDarkMode ? "#0a0a0f" : "#e5e7eb",
            }}
          >
            {loading && (
              <div style={{ textAlign: "center", padding: "3rem" }}>
                <FaSpinner
                  style={{
                    animation: "spin 1s linear infinite",
                    fontSize: "2rem",
                    color: "#8b5cf6",
                  }}
                />
                <p style={{ color: getTextColor(), marginTop: "1rem" }}>
                  Loading PDF...
                </p>
              </div>
            )}
            {error && (
              <div style={{ textAlign: "center", padding: "3rem" }}>
                <FaExclamationTriangle size={48} color="#f59e0b" />
                <p
                  style={{
                    color: getTextColor(),
                    marginTop: "1rem",
                    fontSize: "1rem",
                  }}
                >
                  Failed to load PDF
                </p>
                <button
                  onClick={handleDownload}
                  style={{
                    marginTop: "1rem",
                    padding: "0.5rem 1.5rem",
                    background: "#8b5cf6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Download PDF
                </button>
              </div>
            )}
            {!error && (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div />}
                error={<div />}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {renderAllPages()}
                </div>
              </Document>
            )}
          </div>

          {/* Page counter footer */}
          {numPages > 0 && !error && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
                padding: "0.5rem",
                borderTop: `1px solid ${getBorderColor()}`,
                background: isDarkMode ? "#1e1e2d" : "#f8f9fa",
                fontSize: "0.8rem",
                color: getTextColor(),
              }}
            >
              <span>
                Total Pages: <strong>{numPages}</strong>
              </span>
            </div>
          )}
        </motion.div>
      </motion.div>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .react-pdf__Page {
            background: transparent !important;
          }
          .react-pdf__Page__canvas {
            margin: 0 auto;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            border-radius: 8px;
          }
          /* Smooth scrolling */
          .pdf-viewer-body::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .pdf-viewer-body::-webkit-scrollbar-track {
            background: #1e1e2d;
            border-radius: 10px;
          }
          .pdf-viewer-body::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #8b5cf6, #3b82f6);
            border-radius: 10px;
          }
        `}
      </style>
    </AnimatePresence>
  );
};

export default PDFViewer;

